/**
 * Headless "Send" labelling for campaign projects, shared by the campaign
 * workspace and the Results page's per-send breakdown so a send reads the
 * same name in both places.
 *
 * "Send" (a business/UI term, see the campaign-projects glossary) applies
 * only to email-channel cadence steps; callers pass email steps only.
 */

/** The fields a send's label depends on. */
export interface SendLabelInput {
  id: string;
  /** 1-based ordinal in the project's cadence. */
  order: number;
  /** Set on a reminder: the tranche step it follows. */
  follows_step_id: string | null;
}

/** Tranches in split order (own steps, not reminders), used both to number
 * "Tranche N" labels and to resolve which tranche a reminder follows. */
export function tranchesOf<T extends SendLabelInput>(sends: T[]): T[] {
  return sends
    .filter((s) => !s.follows_step_id)
    .slice()
    .sort((a, b) => a.order - b.order);
}

/** A step another step can follow up: its id and own name (a mailing's or
 * call round's "Mailing 1 – TIME pack"). */
export interface FollowUpSource {
  id: string;
  name: string | null;
}

/** "Mailing 1 – TIME pack" -> "Mailing 1": the part before the en-dash
 *  subtitle, which is how people refer to a step in "Follow-up to ...". */
export function shortStepName(name: string | null | undefined): string {
  if (!name) return "an earlier step";
  // A backend step label is "<campaign> — <step name>"; keep the step part.
  const stepName = name.split(" — ").pop() ?? name;
  return stepName.split(" – ")[0];
}

/** "Follow-up to Mailing 1": a step following up a step that isn't an email
 *  tranche (product owner, 15 Oct 2026 — never "Reminder"). */
export function followUpLabel(sourceName: string | null | undefined): string {
  return `Follow-up to ${shortStepName(sourceName)}`;
}

/** A send's own display label — "Tranche N" for a plain tranche, "Tranche N
 * Reminder" for its paired reminder (making clear which tranche it
 * follows), per the glossary; "Follow-up to Mailing 1" for an email that
 * follows one of ``followUpSources`` (a mailing or call round). Tranche
 * labels are only meaningful once there are 2+ sends — callers gate on that
 * separately. */
export function sendLabel(
  step: SendLabelInput,
  sends: SendLabelInput[],
  followUpSources: FollowUpSource[] = [],
): string {
  const tranches = tranchesOf(sends);
  if (step.follows_step_id) {
    const idx = tranches.findIndex((t) => t.id === step.follows_step_id);
    if (idx >= 0) return `Tranche ${idx + 1} Reminder`;
    const source = followUpSources.find((s) => s.id === step.follows_step_id);
    return source ? followUpLabel(source.name) : "Reminder";
  }
  const idx = tranches.findIndex((t) => t.id === step.id);
  return idx >= 0 ? `Tranche ${idx + 1}` : "Send";
}

/** Master-detail row order: each tranche immediately followed by its own
 * reminder (if any), regardless of raw creation-order position — clearer to
 * scan than the backend's "all tranches, then all reminders" creation order. */
export function sendRows<T extends SendLabelInput>(sends: T[]): T[] {
  const tranches = tranchesOf(sends);
  const rows: T[] = [];
  for (const t of tranches) {
    rows.push(t);
    const reminder = sends.find((s) => s.follows_step_id === t.id);
    if (reminder) rows.push(reminder);
  }
  return rows;
}

/** Whether a send has actually gone out: Mailchimp has reported a send time
 * for it. Deliberately not `status === "closed"` — nothing ever moves a step
 * to closed (a sent step stays "active" for good, which
 * generate_reminder_send depends on), so a status-based check reads "0 sent"
 * on a fully-sent project. Nor `activated_at`: that marks the audience being
 * tagged in Mailchimp, which can precede the actual send. */
export function hasSent(step: { sent_at: string | null }): boolean {
  return step.sent_at !== null;
}
